/**
 * Pure DOM-free utility for computing region boundary pixels.
 *
 * A pixel at (x, y) is "on the boundary" if:
 *   - its labelPlane value equals targetLabel, AND
 *   - at least one of its 4-connected neighbours has a different label value
 *     (or the pixel is at the image edge, where the neighbour is implicitly
 *     "outside" and therefore different).
 *
 * The function returns a Uint8Array of `width * height` bytes. A byte is 1
 * if the corresponding pixel is a boundary pixel for targetLabel, 0 otherwise.
 *
 * Principle A6 portability: no DOM, no window/document references.
 */
export function computeRegionBoundary(
  labelPlane: Uint8Array,
  width: number,
  height: number,
  targetLabel: number,
): Uint8Array {
  const result = new Uint8Array(width * height);

  if (width <= 0 || height <= 0 || labelPlane.length < width * height) {
    return result;
  }

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      if (labelPlane[idx] !== targetLabel) continue;

      // Check 4-connected neighbours. Edge pixels are treated as having a
      // different (virtual) label so the image boundary is included.
      const top    = y === 0          ? -1 : labelPlane[(y - 1) * width + x];
      const bottom = y === height - 1 ? -1 : labelPlane[(y + 1) * width + x];
      const left   = x === 0          ? -1 : labelPlane[y * width + (x - 1)];
      const right  = x === width - 1  ? -1 : labelPlane[y * width + (x + 1)];

      if (top !== targetLabel || bottom !== targetLabel ||
          left !== targetLabel || right !== targetLabel) {
        result[idx] = 1;
      }
    }
  }

  return result;
}
